import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useTheme } from "../../contexts/ThemeContext";
import { userLogout } from "../../helper/global";

const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
const WARNING_TIMEOUT_MS = 58 * 60 * 1000;

const ACTIVITY_EVENTS = [
    "mousemove",
    "mousedown",
    "keydown",
    "scroll",
    "touchstart",
];

export default function IdleTimer({ isAuthenticated }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [showWarning, setShowWarning] = useState(false);
    const warningTimer = useRef(null);
    const logoutTimer = useRef(null);

    const handleLogout = useCallback(() => {
        setShowWarning(false);
        userLogout(dispatch, navigate);
    }, [dispatch, navigate]);

    const resetTimers = useCallback(() => {
        clearTimeout(warningTimer.current);
        clearTimeout(logoutTimer.current);
        setShowWarning(false);

        warningTimer.current = setTimeout(
            () => setShowWarning(true),
            WARNING_TIMEOUT_MS,
        );

        logoutTimer.current = setTimeout(handleLogout, IDLE_TIMEOUT_MS);
    }, [handleLogout]);

    useEffect(() => {
        if (!isAuthenticated) {
            clearTimeout(warningTimer.current);
            clearTimeout(logoutTimer.current);
            setShowWarning(false);
            return;
        }

        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, resetTimers));
        resetTimers();

        return () => {
            ACTIVITY_EVENTS.forEach((e) =>
                window.removeEventListener(e, resetTimers),
            );
            clearTimeout(warningTimer.current);
            clearTimeout(logoutTimer.current);
        };
    }, [isAuthenticated, resetTimers]);

    if (!isAuthenticated) return null;

    return (
        <Dialog open={showWarning} disableEscapeKeyDown>
            <DialogTitle>Session Expiring Soon</DialogTitle>
            <DialogContent sx={{ width: { xs: "auto", md: "450px" } }}>
                <DialogContentText>
                    You will be logged out in 2 minutes due to inactivity. Do
                    you want to stay logged in?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleLogout}
                    sx={{ color: theme.palette.color.button }}
                >
                    Log Out
                </Button>
                <Button
                    onClick={resetTimers}
                    sx={{ color: theme.palette.color.button }}
                >
                    Stay Logged In
                </Button>
            </DialogActions>
        </Dialog>
    );
}
